import { useEffect, useState } from "react";
import { Button } from "../../../components/Button/Button.tsx";
import { BaseClickableIcon } from "../../../components/Icon/BaseClickableIcon.tsx";
import { Spinner } from "../../../components/Spinner/Spinner.tsx";
import { useAppMutation } from "../../../http/useAppMutation.ts";
import { MODEL_TYPE_LABELS } from "../../Board/display.ts";
import { OBJECT_MODEL_TYPE } from "../../constants.ts";
import type { CharacterModel, ObjectModel } from "../../types.ts";
import { ModelCreationLayout } from "../components/ModelCreationLayout.tsx";
import { ModelFormColumn } from "../components/ModelFormColumn.tsx";

export const ObjectAndCharacterDescriptionForm = ({
  model,
  onClickNext,
  onClickPrevious,
}: {
  model: CharacterModel | ObjectModel;
  onClickNext: () => void;
  onClickPrevious: () => void;
}) => {
  const [
    isWaitingForDescriptionGeneration,
    setIsWaitingForDescriptionGeneration,
  ] = useState(model.element_description === null);
  const [elementDescription, setElementDescription] = useState(
    model.element_description ?? "",
  );
  useEffect(() => {
    if (isWaitingForDescriptionGeneration && model.element_description) {
      setElementDescription(model.element_description);
      setIsWaitingForDescriptionGeneration(false);
    }
  }, [model.element_description, isWaitingForDescriptionGeneration]);

  const { mutate: updateModel, isLoading: isUpdateModelLoading } =
    useAppMutation({
      path: "references/update",
      invalidate: [`references/${model.uuid}`],
    }).mutation;

  return (
    <ModelCreationLayout
      showModelPreview
      title={`Describe your ${model.type}`}
      onBack={onClickPrevious}
    >
      <ModelFormColumn>
        <div className="flex-col gap-1000 items-center">
          <div className="w-full flex-col gap-400">
            <div className="relative flex-row items-center">
              <input
                type="text"
                className="w-full h-1000 py-200 px-400 rounded-100 border body-lg-default text-primary placeholder:text-disabled"
                placeholder={
                  model.type === OBJECT_MODEL_TYPE
                    ? "Minimalist black rectangular bag"
                    : "Young blonde woman"
                }
                value={elementDescription}
                onChange={(event) => {
                  setElementDescription(event.target.value);
                }}
              />
              {isWaitingForDescriptionGeneration && (
                <>
                  <div className="w-full h-full absolute left-0 rounded-100 bg-input-surface-rest flex-row-center gap-200">
                    <Spinner size="sm" />
                    <div className="text-disabled">Generating description</div>
                  </div>
                  <BaseClickableIcon
                    name="X"
                    className="absolute right-0"
                    onClick={() => setIsWaitingForDescriptionGeneration(false)}
                  />
                </>
              )}
            </div>
          </div>
          <Button
            className="w-full"
            size="md"
            disabled={elementDescription.isEmpty()}
            loading={isUpdateModelLoading}
            onClick={() => {
              if (model.element_description !== elementDescription) {
                updateModel(
                  {
                    uuid: model.uuid,
                    element_description: elementDescription,
                  },
                  {
                    onSuccess: onClickNext,
                  },
                );
              } else {
                onClickNext();
              }
            }}
          >
            {elementDescription.isEmpty()
              ? `Describe your ${MODEL_TYPE_LABELS[model.type]}`
              : `Create your ${MODEL_TYPE_LABELS[model.type]}`}
          </Button>
        </div>
      </ModelFormColumn>
    </ModelCreationLayout>
  );
};
